/**
 * Dark mode inside a message body (ADR 0081, issue #55).
 *
 * When the app is dark and the sender did not style the message for dark
 * themselves, the message is ADAPTED rather than shown as a white card:
 *
 *  - a light background becomes a dark one of the same hue;
 *  - text whose contrast against the background it now sits on is under WCAG
 *    AA (4.5:1) is lightened -- inverted in perceptual lightness, so the
 *    sender's hierarchy (body copy brighter than muted copy) survives, then
 *    pushed until it passes;
 *  - light borders are darkened so a table's rules do not glare;
 *  - images are NEVER touched: no filter, no inversion. A logo is the
 *    sender's, pixel for pixel. An element with a background image keeps its
 *    colours and so does the text on it, because it was designed for the
 *    picture, not for our canvas.
 *
 * The decisions are made on COMPUTED styles, so a colour from the sender's
 * stylesheet, a `bgcolor` attribute, a `<font color>` or inheritance are all
 * seen the way the engine sees them. They are written as a generated
 * stylesheet, not as inline styles: one rule per adapted element, keyed by a
 * `data-ols-dk` attribute (sender attribute selectors are refused by the CSS
 * pass, so no sender rule can target it), `!important`, in a cascade layer --
 * layered important declarations beat the sender's unlayered `!important` --
 * and inside `@media screen`, so paper is always the sender's original light
 * design.
 *
 * Two copies of this algorithm exist and must agree: this module, which the
 * page runs over a srcdoc frame it can reach (ADR 0017), and the block between
 * the `ols:dark-adapt` markers in `backend/internal/usercontent/frame.js`,
 * which the isolated frame runs over itself (ADR 0078), because there the
 * page cannot reach in. `darkAdapt.test.ts` evaluates that block and checks
 * both give the same answers.
 */

export interface RGBA {
  r: number;
  g: number;
  b: number;
  a: number;
}

/** The canvas a dark message sits on: the app's dark raised surface (neutral-900). */
export const DARK_CANVAS: RGBA = { r: 0x17, g: 0x1c, b: 0x23, a: 1 };

/** WCAG AA for body text. */
export const MIN_CONTRAST = 4.5;

/** A background at or above this relative luminance is "light" and is darkened. */
export const LIGHT_BACKGROUND = 0.4;

/** Elements past this many are left alone: adaptation is linear, but a page is not a database. */
export const MAX_ADAPTED_ELEMENTS = 20000;

/** The class the frame's root carries when adaptation is wanted. */
export const AUTO_DARK_CLASS = 'ols-dark-auto';

/** The attribute each adapted element is keyed by. */
export const ADAPT_ATTRIBUTE = 'data-ols-dk';

/**
 * Parses a computed colour: `rgb(r, g, b)`, `rgba(r, g, b, a)`, or the
 * space-separated `rgb(r g b / a)`. Anything else (a `color()` or `oklch()`
 * an engine might report) is not understood and is left alone.
 */
export function parseColor(value: string | null | undefined): RGBA | null {
  if (!value) return null;
  const m = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)\s*(?:[,/]\s*([\d.]+%?)\s*)?\)$/i.exec(value.trim());
  if (!m) return null;
  let a = 1;
  if (m[4] !== undefined) a = m[4].endsWith('%') ? parseFloat(m[4]) / 100 : parseFloat(m[4]);
  const c = { r: parseFloat(m[1]!), g: parseFloat(m[2]!), b: parseFloat(m[3]!), a };
  if (![c.r, c.g, c.b, c.a].every(Number.isFinite)) return null;
  return c;
}

function toLinear(v: number): number {
  const s = v / 255;
  return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

/** WCAG 2.x relative luminance. */
export function luminance(c: RGBA): number {
  return 0.2126 * toLinear(c.r) + 0.7152 * toLinear(c.g) + 0.0722 * toLinear(c.b);
}

/** WCAG 2.x contrast ratio, 1..21. */
export function contrast(a: RGBA, b: RGBA): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** `fg` painted at its alpha over the opaque `bg`. */
export function over(fg: RGBA, bg: RGBA): RGBA {
  const a = Math.max(0, Math.min(1, fg.a));
  return {
    r: fg.r * a + bg.r * (1 - a),
    g: fg.g * a + bg.g * (1 - a),
    b: fg.b * a + bg.b * (1 - a),
    a: 1,
  };
}

interface Lab {
  L: number;
  A: number;
  B: number;
}

function toLab(c: RGBA): Lab {
  const r = toLinear(c.r);
  const g = toLinear(c.g);
  const b = toLinear(c.b);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return {
    L: 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    A: 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    B: 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  };
}

function unchannel(v: number): number {
  const s = v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
  return s * 255;
}

/** OKLab to sRGB, or null when the colour is outside the sRGB gamut. */
function fromLab(lab: Lab): RGBA | null {
  const l = Math.pow(lab.L + 0.3963377774 * lab.A + 0.2158037573 * lab.B, 3);
  const m = Math.pow(lab.L - 0.1055613458 * lab.A - 0.0638541728 * lab.B, 3);
  const s = Math.pow(lab.L - 0.0894841775 * lab.A - 1.291485548 * lab.B, 3);
  const r = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  const g = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  const b = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s;
  const eps = 0.0005;
  if (r < -eps || r > 1 + eps || g < -eps || g > 1 + eps || b < -eps || b > 1 + eps) return null;
  const clamp = (v: number) => Math.max(0, Math.min(1, v));
  return { r: unchannel(clamp(r)), g: unchannel(clamp(g)), b: unchannel(clamp(b)), a: 1 };
}

/**
 * The colour with lightness L and this hue, with as much of the original
 * chroma (scaled by `keep`) as fits in sRGB.
 */
function withLightness(lab: Lab, L: number, keep: number): RGBA {
  for (let k = keep; k > 0.01; k *= 0.8) {
    const c = fromLab({ L, A: lab.A * k, B: lab.B * k });
    if (c) return c;
  }
  return fromLab({ L, A: 0, B: 0 }) ?? { r: L * 255, g: L * 255, b: L * 255, a: 1 };
}

function round(c: RGBA): RGBA {
  return { r: Math.round(c.r), g: Math.round(c.g), b: Math.round(c.b), a: 1 };
}

/**
 * A light background's dark twin, or null to keep it.
 *
 * Luminance decides WHETHER (a saturated mid-tone button -- #3b82f6, #ea580c
 * -- is not light and keeps its colour, and the white text on it stays
 * right); OKLab decides HOW, so the hue survives: pale yellow becomes a dark
 * olive, not grey. White lands on the canvas itself; the lighter the
 * original, the closer to the canvas, so a grey page with a white card keeps
 * a visible card.
 */
export function darkBackground(c: RGBA): RGBA | null {
  const lum = luminance(c);
  if (lum < LIGHT_BACKGROUND) return null;
  const t = (lum - LIGHT_BACKGROUND) / (1 - LIGHT_BACKGROUND);
  const target = 0.045 - t * 0.034; // luminance: 0.045 (a mid-light tint) .. 0.011 (the canvas)
  const lab = toLab(c);
  let lo = 0;
  let hi = 1;
  let best = withLightness(lab, 0.25, 0.55);
  for (let i = 0; i < 18; i++) {
    const mid = (lo + hi) / 2;
    best = withLightness(lab, mid, 0.55);
    if (luminance(best) < target) lo = mid;
    else hi = mid;
  }
  return round(best);
}

/**
 * Text that is readable on `bg`, or null when `fg` already is.
 *
 * Dark text is inverted in lightness (L' = 0.95 - 0.5 L), which keeps the
 * ORDER of the sender's greys -- #111 body copy ends brighter than #6b7280
 * muted copy -- and then lifted in small steps until it reaches MIN_CONTRAST.
 * Nothing that already passes is changed: white text on a dark header stays
 * white, a link colour that reads on the canvas stays the sender's.
 */
export function readableText(fg: RGBA, bg: RGBA): RGBA | null {
  const solid = fg.a < 1 ? over(fg, bg) : fg;
  if (contrast(solid, bg) >= MIN_CONTRAST) return null;
  const lab = toLab(solid);
  const darkBg = luminance(bg) < 0.18;
  let L = darkBg ? Math.max(0.95 - 0.5 * lab.L, lab.L) : Math.min(0.5 * (1 - lab.L), lab.L);
  let out = round(withLightness(lab, L, 0.9));
  for (let i = 0; i < 60 && contrast(out, bg) < MIN_CONTRAST; i++) {
    L = darkBg ? Math.min(1, L + 0.01) : Math.max(0, L - 0.01);
    out = round(withLightness(lab, L, 0.9));
  }
  if (contrast(out, bg) < MIN_CONTRAST) {
    out = darkBg ? { r: 236, g: 239, b: 244, a: 1 } : { r: 17, g: 21, b: 28, a: 1 };
  }
  return out;
}

/** A light border's dark twin, a little lighter than a background would be, or null to keep it. */
export function darkBorder(c: RGBA): RGBA | null {
  const lum = luminance(c);
  if (lum < LIGHT_BACKGROUND) return null;
  const t = (lum - LIGHT_BACKGROUND) / (1 - LIGHT_BACKGROUND);
  const lab = toLab(c);
  return round(withLightness(lab, 0.45 - t * 0.12, 0.5));
}

export function cssColor(c: RGBA): string {
  return `rgb(${c.r},${c.g},${c.b})`;
}

const SKIP = new Set(['img', 'svg', 'video', 'picture', 'canvas', 'iframe', 'object', 'embed', 'br', 'wbr', 'hr']);
const SIDES = ['top', 'right', 'bottom', 'left'] as const;

type Effective = RGBA | 'image';

function hasOwnText(el: Element): boolean {
  for (let n = el.firstChild; n; n = n.nextSibling) {
    if (n.nodeType === 3 && /\S/.test(n.nodeValue ?? '')) return true;
  }
  return false;
}

/**
 * Decides every adaptation for the document and returns the stylesheet that
 * applies them (empty when nothing needs to change). Marks each adapted
 * element with ADAPT_ATTRIBUTE. Reads styles; changes nothing else.
 */
export function buildDarkAdaptation(doc: Document, win: Window): { css: string; adapted: number } {
  const root = doc.querySelector('.ols-msg') ?? doc.body;
  if (!root) return { css: '', adapted: 0 };
  const effective = new Map<Element, Effective>();
  const rules: string[] = [];
  const elements = [root, ...Array.from(root.querySelectorAll('*'))].slice(0, MAX_ADAPTED_ELEMENTS);
  for (const el of elements) {
    const parent = el.parentElement;
    const inherited: Effective = (parent && effective.get(parent)) || DARK_CANVAS;
    const tag = el.localName;
    if (SKIP.has(tag)) {
      effective.set(el, inherited);
      continue;
    }
    const cs = win.getComputedStyle(el);
    const decls: string[] = [];
    let here: Effective = inherited;
    if (cs.backgroundImage && cs.backgroundImage !== 'none') {
      here = 'image';
    } else {
      const bg = parseColor(cs.backgroundColor);
      if (bg && bg.a >= 0.05) {
        const base = inherited === 'image' ? DARK_CANVAS : inherited;
        const dark = darkBackground(bg);
        if (dark) {
          decls.push(`background-color:${cssColor(dark)}!important`);
          here = bg.a >= 0.5 ? dark : over({ ...dark, a: bg.a }, base);
        } else {
          here = bg.a >= 0.5 ? { ...bg, a: 1 } : over(bg, base);
        }
      }
    }
    effective.set(el, here);
    if (here !== 'image') {
      if (hasOwnText(el)) {
        const fg = parseColor(cs.color);
        const next = fg ? readableText(fg, here) : null;
        if (next) decls.push(`color:${cssColor(next)}!important`);
      }
      for (const side of SIDES) {
        if (cs.getPropertyValue(`border-${side}-style`) === 'none') continue;
        if (!(parseFloat(cs.getPropertyValue(`border-${side}-width`)) > 0)) continue;
        const bc = parseColor(cs.getPropertyValue(`border-${side}-color`));
        const next = bc && bc.a > 0 ? darkBorder(bc) : null;
        if (next) decls.push(`border-${side}-color:${cssColor(next)}!important`);
      }
    }
    if (decls.length > 0) {
      const key = String(rules.length);
      el.setAttribute(ADAPT_ATTRIBUTE, key);
      rules.push(`:root.${AUTO_DARK_CLASS} [${ADAPT_ATTRIBUTE}="${key}"]{${decls.join(';')}}`);
    }
  }
  if (rules.length === 0) return { css: '', adapted: 0 };
  return { css: `@layer ols-dark-adapt{@media screen{${rules.join('')}}}`, adapted: rules.length };
}

/**
 * Adds a stylesheet to a document the page can reach, without an inline
 * `<style>`: the srcdoc frame inherits the app shell's CSP, whose
 * `style-src-elem` admits only the frame reset's hash (ADR 0049). A
 * constructed stylesheet is not governed by style-src, and nothing about it
 * can load anything: `replaceSync` refuses `@import`, and the text is either
 * the sanitizer's output or this module's.
 *
 * It must be constructed in the frame's own realm, or the engine refuses to
 * adopt it.
 */
export function adoptStylesheet(doc: Document, css: string): boolean {
  if (!css) return true;
  const view = doc.defaultView as (Window & typeof globalThis) | null;
  const Sheet = view?.CSSStyleSheet;
  if (!Sheet || !('adoptedStyleSheets' in doc)) return false;
  try {
    const sheet = new Sheet();
    sheet.replaceSync(css);
    doc.adoptedStyleSheets = [...doc.adoptedStyleSheets, sheet];
    return true;
  } catch {
    return false;
  }
}

/** Runs the adaptation over a frame document the page can reach. */
export function applyDarkAdaptation(doc: Document): number {
  const win = doc.defaultView;
  // A frame mid-navigation has a document with no root yet.
  if (!win || !doc.documentElement?.classList.contains(AUTO_DARK_CLASS)) return 0;
  const { css, adapted } = buildDarkAdaptation(doc, win);
  if (css) adoptStylesheet(doc, css);
  return adapted;
}
